<template>
  <div class="lea-view">

    <!-- ─── Activité principale et secondaire ─── -->
    <div class="lea-section">
      <div class="lea-section-header" @click="toggleSection('categories')">
        <h3 class="lea-section-title">{{ $t('dash.presenceManagement.location.activity.categories.title') }}</h3>
        <i class="material-icons lea-chevron">{{ sectionsOpen.categories ? 'expand_less' : 'expand_more' }}</i>
      </div>

      <div v-show="sectionsOpen.categories" class="lea-section-content">
        <div class="lea-categories-layout">
          <!-- Autocomplete search -->
          <div class="lea-search-wrapper" v-if="currentNetwork !== 'instagram'">
            <input
              v-model="categorySearch"
              type="text"
              class="lea-search-input"
              :placeholder="$t('dash.presenceManagement.location.activity.categories.search')"
              @focus="showSuggestions = true"
              @blur="onSearchBlur"
            />
            <i v-if="loadingSuggestions" class="material-icons lea-search-icon lea-spinner">sync</i>
            <i v-else class="material-icons lea-search-icon">search</i>
            <div v-if="showSuggestions && suggestions.length" class="lea-suggestions">
              <div
                v-for="cat in suggestions"
                :key="cat.name"
                class="lea-suggestion-item"
                @mousedown.prevent="selectCategory(cat)"
              >
                {{ cat.displayName }}
              </div>
            </div>
          </div>
          <div v-else class="lep-sab-notice" style="margin-bottom: 16px;">
            <i class="material-icons">info_outline</i>
            <span>{{ $t('dash.presenceManagement.location.activity.instagramCategoryHint') }}</span>
          </div>

          <!-- Selected categories -->
          <div class="lea-selected-categories">
            <div v-if="primaryCategory" class="lea-category-group">
              <span class="lea-category-label">{{ $t('dash.presenceManagement.location.activity.categories.primary') }}</span>
              <span class="lea-tag lea-tag--primary">
                {{ primaryCategory.displayName }}
                <button v-if="currentNetwork !== 'instagram'" class="lea-tag-remove" @click="removePrimaryCategory" :title="$t('dash.presenceManagement.location.activity.categories.remove')">×</button>
              </span>
            </div>

            <div v-if="additionalCategories.length" class="lea-category-group">
              <span class="lea-category-label">{{ $t('dash.presenceManagement.location.activity.categories.secondary') }}</span>
              <span
                v-for="cat in additionalCategories"
                :key="cat.name"
                class="lea-tag lea-tag--secondary lea-tag--clickable"
                @click="promoteToPrimaryCategory(cat.name)"
                :title="$t('dash.presenceManagement.location.activity.setPrimaryTooltip')"
              >
                {{ cat.displayName }}
                <button class="lea-tag-remove" @click.stop="removeAdditionalCategory(cat.name)">×</button>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ─── Facebook-specific sections: Spécialités FIRST, Gamme de Prix SECOND ─── -->
    <template v-if="currentNetwork === 'facebook'">
      <!-- ─── Spécialités & Services Personnalisés (jusqu'à 10) ─── -->
      <div class="lea-section">
        <div class="lea-section-header" @click="toggleSection('specialties')">
          <h3 class="lea-section-title">{{ $t('dash.presenceManagement.location.activity.specialtiesTitle', { count: localCustomSpecialties.length }) }}</h3>
          <i class="material-icons lea-chevron">{{ sectionsOpen.specialties ? 'expand_less' : 'expand_more' }}</i>
        </div>

        <div v-show="sectionsOpen.specialties" class="lea-section-content">
          <div class="lea-specialties-header">
            <p class="lea-specialties-subtext">{{ $t('dash.presenceManagement.location.activity.specialtiesSubtext') }}</p>
            <button 
              type="button" 
              class="lea-btn-add-specialty"
              v-if="localCustomSpecialties.length < 10" 
              @click="addCustomSpecialty"
            >
              <i class="material-icons">add</i>
              <span>{{ $t('dash.presenceManagement.location.activity.addSpecialty') }}</span>
            </button>
          </div>

          <div v-for="(spec, idx) in localCustomSpecialties" :key="idx" class="lea-specialty-card">
            <div class="lea-specialty-fields">
              <div class="lea-field-item">
                <label class="lea-sublabel">{{ $t('dash.presenceManagement.location.activity.specialtyNameLabel') }}</label>
                <input 
                  type="text" 
                  v-model="spec.title" 
                  class="lea-input-full" 
                  :placeholder="$t('dash.presenceManagement.location.activity.specialtyNamePlaceholder')" 
                />
              </div>
              <div class="lea-field-item">
                <label class="lea-sublabel">{{ $t('dash.presenceManagement.location.activity.specialtyDescLabel') }}</label>
                <div class="lea-specialty-desc-wrapper">
                  <input 
                    type="text" 
                    v-model="spec.description" 
                    class="lea-input-full" 
                    maxlength="150"
                    :placeholder="$t('dash.presenceManagement.location.activity.specialtyDescPlaceholder')" 
                  />
                  <small class="lea-char-count">{{ (spec.description || '').length }}/150</small>
                </div>
                  <button type="button" class="lea-btn-remove-specialty-text" @click="removeCustomSpecialty(idx)">
                    <div class="lea-btn-inner">
                      <i class="material-icons">delete_outline</i>
                      <span>{{ $t('dash.presenceManagement.location.activity.removeSpecialty') }}</span>
                    </div>
                  </button> 
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ─── Gamme de prix & Positionnement (placé sous spécialités & prestations) ─── -->
      <div class="lea-section">
        <div class="lea-section-header" @click="toggleSection('priceRange')">
          <h3 class="lea-section-title">{{ $t('dash.presenceManagement.location.activity.priceRangeTitle') }}</h3>
          <i class="material-icons lea-chevron">{{ sectionsOpen.priceRange ? 'expand_less' : 'expand_more' }}</i>
        </div>

        <div v-show="sectionsOpen.priceRange" class="lea-section-content">
          <div class="lea-price-range-options">
            <button
              type="button"
              v-for="opt in priceRangeOptions"
              :key="opt.value"
              class="lea-price-chip"
              :class="{ 'lea-price-chip--active': localPriceRange === opt.value }"
              @click="localPriceRange = opt.value"
            >
              <span class="lea-price-symbol">{{ opt.symbol }}</span>
              <span class="lea-price-label">{{ $t(opt.labelKey) }}</span>
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- ─── Dynamic Service Types (GBP category-specific sub-services) ─── -->
    <div v-if="currentNetwork !== 'facebook' && currentNetwork !== 'instagram' && primaryCategory && availableServiceTypes.length" class="lea-section">
      <div class="lea-section-header" @click="toggleSection('services')">
        <h3 class="lea-section-title">{{ $t('dash.presenceManagement.location.activity.establishmentServices') }}</h3>
        <i class="material-icons lea-chevron">{{ sectionsOpen.services ? 'expand_less' : 'expand_more' }}</i>
      </div>

      <div v-show="sectionsOpen.services" class="lea-section-content">
        <div class="lea-chips-row">
          <button
            v-for="service in availableServiceTypes"
            :key="service.id"
            class="lea-chip"
            :class="{ 'lea-chip--active': selectedServiceTypes.includes(service.id) }"
            @click="toggleServiceType(service.id)"
          >
            {{ service.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- ─── Attribute groups (GBP accessibility, payments, ambiance, dietary) ─── -->
    <template v-if="currentNetwork !== 'facebook' && currentNetwork !== 'instagram'">
      <div
        v-for="group in availableAttributeGroups"
        :key="group.key"
        class="lea-section"
      >
        <div class="lea-section-header" @click="toggleSection(group.key)">
          <h3 class="lea-section-title">{{ $t(`dash.presenceManagement.location.activity.${group.key}.title`) }}</h3>
          <i class="material-icons lea-chevron">{{ sectionsOpen[group.key] ? 'expand_less' : 'expand_more' }}</i>
        </div>

        <div v-show="sectionsOpen[group.key]" class="lea-section-content">
          <div class="lea-chips-row">
            <button
              v-for="attr in group.attributes"
              :key="attr.id"
              class="lea-chip"
              :class="{ 'lea-chip--active': attributes[attr.id] }"
              @click="toggleAttribute(attr.id)"
            >
              {{ $t(`dash.presenceManagement.location.activity.${group.key}.${attr.labelKey}`) }}
            </button>
          </div>
        </div>
      </div>
    </template>

  </div>
</template>

<script>
import axios from 'axios'

const MOCK_CATEGORIES = [
  { name: 'categories/gcid:bakery', displayName: 'Boulangerie' },
  { name: 'categories/gcid:pastry_shop', displayName: 'Pâtisserie' },
  { name: 'categories/gcid:tearoom', displayName: 'Salon de thé' },
  { name: 'categories/gcid:restaurant', displayName: 'Restaurant' },
  { name: 'categories/gcid:cafe', displayName: 'Café' },
  { name: 'categories/gcid:bar', displayName: 'Bar' },
  { name: 'categories/gcid:supermarket', displayName: 'Supermarché' },
  { name: 'categories/gcid:pharmacy', displayName: 'Pharmacie' },
  { name: 'categories/gcid:hair_salon', displayName: 'Salon de coiffure' },
  { name: 'categories/gcid:hotel', displayName: 'Hôtel' },
  { name: 'categories/gcid:gym', displayName: 'Salle de sport' },
  { name: 'categories/gcid:dentist', displayName: 'Dentiste' },
  { name: 'categories/gcid:doctor', displayName: 'Médecin' },
  { name: 'categories/gcid:computer_support_and_services', displayName: 'Assistance et services informatiques' },
  { name: 'categories/gcid:lawyer', displayName: 'Avocat' },
  { name: 'categories/gcid:accountant', displayName: 'Expert-comptable' },
  { name: 'categories/gcid:real_estate_agency', displayName: 'Agence immobilière' },
  { name: 'categories/gcid:clothing_store', displayName: 'Boutique de vêtements' },
  { name: 'categories/gcid:bookstore', displayName: 'Librairie' },
  { name: 'categories/gcid:florist', displayName: 'Fleuriste' }
]

const ATTRIBUTE_GROUPS = [
  {
    key: 'serviceTypes',
    attributes: [
      { id: 'has_in_store_shopping', labelKey: 'inStoreShopping' },
      { id: 'has_in_store_pickup',   labelKey: 'inStorePickup' },
      { id: 'has_delivery',          labelKey: 'delivery' },
      { id: 'has_dine_in',           labelKey: 'dineIn' },
      { id: 'has_takeout',           labelKey: 'takeout' }
    ]
  },
  {
    key: 'accessibility',
    attributes: [
      { id: 'wheelchair_accessible_entrance', labelKey: 'wheelchairEntrance' },
      { id: 'has_parking',                    labelKey: 'parking' },
      { id: 'has_restroom',                   labelKey: 'restroom' }
    ]
  },
  {
    key: 'payments',
    attributes: [
      { id: 'pay_credit_card',   labelKey: 'creditCard' },
      { id: 'pay_apple_pay',     labelKey: 'applePay' },
      { id: 'pay_google_pay',    labelKey: 'googlePay' },
      { id: 'pay_meal_vouchers', labelKey: 'mealVouchers' }
    ]
  },
  {
    key: 'ambiance',
    attributes: [
      { id: 'good_for_groups',   labelKey: 'goodForGroups' },
      { id: 'good_for_kids',     labelKey: 'goodForKids' },
      { id: 'has_outdoor_seating', labelKey: 'outdoorSeating' },
      { id: 'has_live_music',    labelKey: 'liveMusic' },
      { id: 'is_romantic',       labelKey: 'romantic' }
    ]
  },
  {
    key: 'dietary',
    attributes: [
      { id: 'serves_vegetarian',  labelKey: 'vegetarian' },
      { id: 'serves_vegan',       labelKey: 'vegan' },
      { id: 'serves_halal',       labelKey: 'halal' },
      { id: 'serves_kosher',      labelKey: 'kosher' },
      { id: 'serves_gluten_free', labelKey: 'glutenFree' }
    ]
  }
]

const CATEGORY_SERVICES = {
  'categories/gcid:bakery': [
    { id: 'job_type_id:in_store_shopping', label: 'Achats en magasin' },
    { id: 'job_type_id:in_store_pickup', label: 'Retrait en magasin' },
    { id: 'job_type_id:delivery', label: 'Livraison' }
  ],
  'categories/gcid:pastry_shop': [
    { id: 'job_type_id:in_store_pickup', label: 'Retrait en magasin' },
    { id: 'job_type_id:delivery', label: 'Livraison' }
  ],
  'categories/gcid:tearoom': [
    { id: 'job_type_id:dine_in', label: 'Sur place' },
    { id: 'job_type_id:takeout', label: 'À emporter' }
  ],
  'categories/gcid:restaurant': [
    { id: 'job_type_id:dine_in', label: 'Sur place' },
    { id: 'job_type_id:takeout', label: 'À emporter' },
    { id: 'job_type_id:delivery', label: 'Livraison' }
  ],
  'categories/gcid:cafe': [
    { id: 'job_type_id:dine_in', label: 'Sur place' },
    { id: 'job_type_id:takeout', label: 'À emporter' }
  ],
  'categories/gcid:computer_support_and_services': [
    { id: 'job_type_id:installation', label: 'Installation de matériel' },
    { id: 'job_type_id:it_consulting', label: 'Conseil informatique' },
    { id: 'job_type_id:repair', label: 'Dépannage système' }
  ],
  'categories/gcid:hair_salon': [
    { id: 'job_type_id:haircut', label: 'Coupe sur cheveux courts/longs' },
    { id: 'job_type_id:hair_coloring', label: 'Coloration végétale' },
    { id: 'job_type_id:barber', label: 'Taille de la barbe' }
  ]
}

/**

/**
 * Converts a GBP attributes array to a flat boolean map keyed by attribute id.
 * GBP format: [{ name: 'attributes/has_delivery', values: [true], valueType: 'BOOL' }]
 * @param {Array} gbpAttributes
 * @returns {Object}
 */
function gbpAttributesToMap(gbpAttributes) {
  const map = {}
  if (!Array.isArray(gbpAttributes)) return map
  gbpAttributes.forEach(attr => {
    if (!attr.name) return
    const id = attr.name.replace('attributes/', '')
    const val = Array.isArray(attr.values) ? attr.values[0] : attr.value
    map[id] = val === true || val === 'true'
  })
  return map
}

function mapToGbpAttributes(map) {
  return Object.entries(map)
    .filter(([, v]) => v)
    .map(([id]) => ({ name: `attributes/${id}`, valueType: 'BOOL', values: [true] }))
}

export default {
  name: 'tab-activity',

  props: {
    categories: {
      type: Object,
      default: () => ({})
    },
    gbpAttributes: {
      type: Array,
      default: () => []
    },
    serviceItems: {
      type: Array,
      default: () => []
    },
    isServiceAreaOnly: {
      type: Boolean,
      default: false
    },
    supportedAttributes: {
      type: Array,
      default: () => []
    },
    locationName: {
      type: String,
      default: ''
    },
    locationData: {
      type: Object,
      default: () => ({})
    },
    currentNetwork: {
      type: String,
      default: 'google'
    }
  },

  data() {
    return {
      categorySearch: '',
      showSuggestions: false,
      suggestions: [],
      searchTimeout: null,
      loadingSuggestions: false,
      primaryCategory: this.categories?.primaryCategory
        ? { name: this.categories.primaryCategory.name, displayName: this.categories.primaryCategory.displayName }
        : null,
      additionalCategories: (this.categories?.additionalCategories || []).map(c => ({
        name: c.name, displayName: c.displayName
      })),
      selectedServiceTypes: (() => {
        if (this.serviceItems && this.serviceItems.length > 0) {
          return this.serviceItems
            .map(item => item?.structuredServiceItem?.serviceTypeId)
            .filter(Boolean)
        }
        return (this.categories?.primaryCategory?.serviceTypes || []).map(s => s.serviceTypeId || s.name)
      })(),
      attributes: gbpAttributesToMap(this.gbpAttributes),
      localSupportedAttributes: [...this.supportedAttributes],
      localPriceRange: (this.locationData?.priceRange && this.locationData.priceRange !== '$') ? this.locationData.priceRange : (this.categories?.priceRange && this.categories.priceRange !== '$' ? this.categories.priceRange : 'UNSPECIFIED'),
      localCustomSpecialties: Array.isArray(this.categories?.specialties)
        ? JSON.parse(JSON.stringify(this.categories.specialties))
        : [],
      priceRangeOptions: [
        { value: '$', symbol: '$', labelKey: 'dash.presenceManagement.location.activity.priceRangeOptions.cheap' },
        { value: '$$', symbol: '$$', labelKey: 'dash.presenceManagement.location.activity.priceRangeOptions.moderate' },
        { value: '$$$', symbol: '$$$', labelKey: 'dash.presenceManagement.location.activity.priceRangeOptions.expensive' },
        { value: '$$$$', symbol: '$$$$', labelKey: 'dash.presenceManagement.location.activity.priceRangeOptions.luxury' },
        { value: 'UNSPECIFIED', symbol: '—', labelKey: 'dash.presenceManagement.location.activity.priceRangeOptions.unspecified' }
      ],
      sectionsOpen: {
        categories: true,
        priceRange: true,
        specialties: true,
        services: true,
        accessibility: true,
        payments: true,
        ambiance: false,
        dietary: false
      }
    }
  },

  computed: {
    // filteredCategories() {
    //   const q = this.categorySearch.trim().toLowerCase()
    //   if (!q) return []
    //   return MOCK_CATEGORIES.filter(c => c.displayName.toLowerCase().includes(q)).slice(0, 8)
    // },

    availableServiceTypes() {
      if (!this.primaryCategory) return []
      if (Array.isArray(this.primaryCategory.serviceTypes)) {
        return this.primaryCategory.serviceTypes.map(s => ({
          id: s.serviceTypeId || s.name,
          label: s.displayName
        }))
      }
      const initialCat = this.categories?.primaryCategory
      if (initialCat && initialCat.name === this.primaryCategory.name && Array.isArray(initialCat.serviceTypes)) {
        return initialCat.serviceTypes.map(s => ({
          id: s.serviceTypeId || s.name,
          label: s.displayName
        }))
      }
      return []
    },

    availableAttributeGroups() {
      if (!this.primaryCategory) return []
      const catName = this.primaryCategory.name
      
      const groups = []

      if (!this.isServiceAreaOnly) {
        groups.push({
          key: 'accessibility',
          attributes: [
            { id: 'has_wheelchair_accessible_entrance', labelKey: 'wheelchairEntrance' },
            { id: 'has_onsite_parking',                 labelKey: 'parking' },
            { id: 'has_restroom',                       labelKey: 'restroom' }
          ]
        })
      }

      groups.push({
        key: 'payments',
        attributes: [
          { id: 'pay_credit_card',   labelKey: 'creditCard' },
          { id: 'pay_apple_pay',     labelKey: 'applePay' },
          { id: 'pay_google_pay',    labelKey: 'googlePay' },
          { id: 'pay_meal_vouchers', labelKey: 'mealVouchers' }
        ]
      })

      const isFoodRelated = ['bakery', 'pastry_shop', 'tearoom', 'restaurant', 'cafe', 'bar', 'supermarket']
        .some(suffix => catName?.includes(suffix))

      if (isFoodRelated) {
        groups.push(
          {
            key: 'ambiance',
            attributes: [
              { id: 'good_for_groups',     labelKey: 'goodForGroups' },
              { id: 'good_for_kids',       labelKey: 'goodForKids' },
              { id: 'has_outdoor_seating', labelKey: 'outdoorSeating' },
              { id: 'has_live_music',      labelKey: 'liveMusic' },
              { id: 'is_romantic',         labelKey: 'romantic' }
            ]
          },
          {
            key: 'dietary',
            attributes: [
              { id: 'serves_vegetarian',  labelKey: 'vegetarian' },
              { id: 'serves_vegan',       labelKey: 'vegan' },
              { id: 'serves_halal',       labelKey: 'halal' },
              { id: 'serves_kosher',      labelKey: 'kosher' },
              { id: 'serves_gluten_free', labelKey: 'glutenFree' }
            ]
          }
        )
      }

      // Safe fallback: if localSupportedAttributes is not loaded or empty, show everything
      if (!this.localSupportedAttributes || this.localSupportedAttributes.length === 0) {
        return groups
      }

      // Filter attributes to only show those supported by Google
      return groups
        .map(group => {
          const filtered = group.attributes.filter(attr => {
            const fullId = `attributes/${attr.id}`
            return this.localSupportedAttributes.includes(fullId)
          })
          return {
            ...group,
            attributes: filtered
          }
        })
        .filter(group => group.attributes.length > 0)
    }
  },

  watch: {
    categorySearch(newVal) {
      clearTimeout(this.searchTimeout)
      const q = newVal.trim()
      if (!q) {
        this.suggestions = []
        return
      }
      this.searchTimeout = setTimeout(() => {
        this.fetchCategoriesGmb(q)
      }, 300)
    },
    supportedAttributes(newVal) {
      this.localSupportedAttributes = [...newVal]
    }
  },

  methods: {
    /** Returns current categories and attributes in GBP format. Called by the parent on save. */
    getActivityData() {
      const updatedCategories = {}
      if (this.primaryCategory) {
        updatedCategories.primaryCategory = {
          name: this.primaryCategory.name,
          displayName: this.primaryCategory.displayName
        }
      }
      if (this.additionalCategories.length) {
        updatedCategories.additionalCategories = this.additionalCategories.map(c => ({
          name: c.name, displayName: c.displayName
        }))
      }

      const serviceItems = this.selectedServiceTypes.map(id => ({
        structuredServiceItem: {
          serviceTypeId: id
        }
      }))

      return {
        categories: updatedCategories,
        priceRange: this.localPriceRange,
        specialties: (this.localCustomSpecialties || []).filter(s => s.title.trim() || s.description.trim()),
        serviceItems,
        attributes: mapToGbpAttributes(this.attributes)
      }
    },

    addCustomSpecialty() {
      if (this.localCustomSpecialties.length < 10) {
        this.localCustomSpecialties.push({ title: '', description: '' })
      }
    },

    removeCustomSpecialty(index) {
      this.localCustomSpecialties.splice(index, 1)
    },

    toggleSection(key) {
      this.$set(this.sectionsOpen, key, !this.sectionsOpen[key])
    },

    toggleAttribute(id) {
      this.$set(this.attributes, id, !this.attributes[id])
    },

    toggleServiceType(id) {
      const idx = this.selectedServiceTypes.indexOf(id)
      if (idx === -1) {
        this.selectedServiceTypes.push(id)
      } else {
        this.selectedServiceTypes.splice(idx, 1)
      }
    },

    selectCategory(cat) {
      if (!this.primaryCategory) {
        this.setPrimaryCategory(cat)
      } else if (
        cat.name !== this.primaryCategory.name &&
        !this.additionalCategories.find(c => c.name === cat.name) &&
        this.additionalCategories.length < 9
      ) {
        this.additionalCategories.push(cat)
      }
      this.categorySearch = ''
      this.showSuggestions = false
    },

    setPrimaryCategory(cat) {
      const oldSelected = [...this.selectedServiceTypes]
      this.primaryCategory = cat
      if (cat) {
        let newSupportedIds = []
        if (Array.isArray(cat.serviceTypes)) {
          newSupportedIds = cat.serviceTypes.map(s => s.serviceTypeId || s.name)
        } else {
          newSupportedIds = (CATEGORY_SERVICES[cat.name] || []).map(s => s.id)
        }

        const commonSelected = oldSelected.filter(id => newSupportedIds.includes(id))

        if (commonSelected.length > 0) {
          this.selectedServiceTypes = commonSelected
        } else {
          this.selectedServiceTypes = newSupportedIds
        }

        // Fetch supported attributes for the new category dynamically from the GMB API
        if (this.locationName) {
          const locationId = this.locationName.replace('locations/', '')
          axios.get(
            `${process.env.VUE_APP_API_HOST}/presence-management/locations/${locationId}/attributes?categoryName=${encodeURIComponent(cat.name)}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
              }
            }
          )
          .then(({ data }) => {
            if (data.success && data.supportedAttributes) {
              this.localSupportedAttributes = data.supportedAttributes
            }
          })
          .catch(err => {
            console.error('Failed to fetch new GMB supported attributes:', err)
          })
        }
      } else {
        this.selectedServiceTypes = []
      }
    },

    removePrimaryCategory() {
      if (this.additionalCategories && this.additionalCategories.length > 0) {
        const nextPrimary = this.additionalCategories.shift()
        this.setPrimaryCategory(nextPrimary)
      } else {
        this.setPrimaryCategory(null)
      }
    },

    promoteToPrimaryCategory(name) {
      const idx = this.additionalCategories.findIndex(c => c.name === name)
      if (idx !== -1) {
        const selectedCat = this.additionalCategories.splice(idx, 1)[0]
        if (this.primaryCategory) {
          this.additionalCategories.unshift(this.primaryCategory)
        }
        this.setPrimaryCategory(selectedCat)
      }
    },

    removeAdditionalCategory(name) {
      const idx = this.additionalCategories.findIndex(c => c.name === name)
      if (idx !== -1) this.additionalCategories.splice(idx, 1)
    },

    onSearchBlur() {
      setTimeout(() => { this.showSuggestions = false }, 150)
    },

    async fetchCategoriesGmb(q) {
      this.loadingSuggestions = true
      try {
        const locale = this.$i18n.locale || 'fr'
        const { data } = await axios.get(
          `${process.env.VUE_APP_API_HOST}/presence-management/categories?q=${encodeURIComponent(q)}&locale=${locale}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            }
          }
        )
        if (data.success) {
          this.suggestions = data.categories
        }
      } catch (error) {
        console.error('Error fetching GMB categories:', error)
      } finally {
        this.loadingSuggestions = false
      }
    }
  }
}
</script>

<style scoped>
.lea-view {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* ─── Section ─── */
.lea-section {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(46, 25, 55, 0.07);
}

.lea-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid #f0e8f6;
}

.lea-section-header:hover { background: #fdf9fd; }

.lea-section-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #6a1f4c;
}

.lea-chevron { color: #6a1f4c; font-size: 22px; }

.lea-section-content { padding: 20px 24px; }

/* ─── Categories layout ─── */
.lea-categories-layout {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  flex-wrap: wrap;
}

/* ─── Search ─── */
.lea-search-wrapper {
  position: relative;
  width: 280px;
  flex-shrink: 0;
}

.lea-search-input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e2d5ef;
  border-radius: 8px;
  padding: 9px 36px 9px 14px;
  font-size: 13px;
  color: #3d2a4a;
  background: #fff;
  outline: none;
  transition: border-color 0.2s;
}

.lea-search-input:focus { border-color: #6a1f4c; }

.lea-search-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
  color: #c0afd0;
  pointer-events: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
}

@keyframes lea-spin {
  from { transform: translateY(-50%) rotate(0deg); }
  to { transform: translateY(-50%) rotate(360deg); }
}

.lea-spinner {
  animation: lea-spin 1s linear infinite;
}

.lea-suggestions {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #e2d5ef;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(46, 25, 55, 0.12);
  z-index: 100;
  overflow: hidden;
}

.lea-suggestion-item {
  padding: 9px 14px;
  font-size: 13px;
  color: #3d2a4a;
  cursor: pointer;
  transition: background 0.15s;
}

.lea-suggestion-item:hover { background: #faf5fd; color: #6a1f4c; }

/* ─── Selected categories ─── */
.lea-selected-categories {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
}

.lea-category-group {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.lea-category-label {
  font-size: 12px;
  font-weight: 600;
  color: #9e8dae;
  white-space: nowrap;
}

/* ─── Tags ─── */
.lea-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 20px;
  padding: 5px 10px 5px 14px;
  font-size: 13px;
  font-weight: 600;
}

.lea-tag--primary {
  background: #6a1f4c;
  color: #fff;
}

.lea-tag--secondary {
  background: #ede0f5;
  color: #6a1f4c;
}

.lea-tag--clickable {
  cursor: pointer;
  transition: transform 0.15s, background 0.15s;
}

.lea-tag--clickable:hover {
  background: #e2ccee;
  transform: translateY(-1px);
}

.lea-tag-remove {
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
  border: none !important;
  padding: 0 !important;
  min-width: 0 !important;
  width: 16px !important;
  height: 16px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  border-radius: 50% !important;
  font-size: 14px !important;
  cursor: pointer !important;
  line-height: 1 !important;
  color: inherit !important;
  opacity: 0.7;
  transition: opacity 0.15s;
}

.lea-tag-remove:hover { opacity: 1; }

/* ─── Chips ─── */
.lea-chips-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

button.lea-chip {
  background: transparent !important;
  background-color: transparent !important;
  border: 1.5px solid #e2d5ef !important;
  box-shadow: none !important;
  border-radius: 24px !important;
  padding: 7px 18px !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  color: #6a1f4c !important;
  cursor: pointer !important;
  transition: background-color 0.18s, border-color 0.18s, color 0.18s !important;
  min-width: 0 !important;
  width: auto !important;
}

button.lea-chip:hover {
  border-color: #6a1f4c !important;
  background-color: #faf5fd !important;
}

button.lea-chip--active {
  background-color: #6a1f4c !important;
  border-color: #6a1f4c !important;
  color: #fff !important;
}

button.lea-chip--active:hover {
  background-color: #5a1840 !important;
}

/* ─── Price Range ─── */
.lea-price-range-options {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

button.lea-price-chip {
  display: inline-flex !important;
  align-items: center !important;
  gap: 8px !important;
  background: #ffffff !important;
  background-color: #ffffff !important;
  border: 1.5px solid #e2d5ef !important;
  border-radius: 12px !important;
  padding: 10px 18px !important;
  color: #6a1f4c !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  cursor: pointer !important;
  transition: all 0.18s ease-in-out !important;
  box-shadow: none !important;
  -webkit-box-shadow: none !important;
}

button.lea-price-chip:hover {
  border-color: #6a1f4c !important;
  background-color: #faf5fd !important;
  color: #6a1f4c !important;
}

button.lea-price-chip--active {
  background: #6a1f4c !important;
  background-color: #6a1f4c !important;
  border-color: #6a1f4c !important;
  color: #ffffff !important;
}

button.lea-price-chip--active:hover {
  background: #5a1840 !important;
  background-color: #5a1840 !important;
  color: #ffffff !important;
}

.lea-price-symbol {
  font-weight: 600;
  font-size: 14px;
  color: inherit !important;
}

.lea-price-label {
  font-size: 13px;
  font-weight: 500;
  color: inherit !important;
}

/* ─── Custom Specialties ─── */
.lea-specialties-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 16px;
}

.lea-specialties-subtext {
  margin: 0;
  font-size: 13px;
  color: #8c7d96;
}

.lea-btn-add-specialty {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 6px !important;
  width: auto !important;
  min-width: 0 !important;
  background: #6a1f4c !important;
  color: #fff !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 8px 16px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  cursor: pointer !important;
  white-space: nowrap !important;
}

.lea-btn-add-specialty ::v-deep span {
  text-transform: none !important;
}

.lea-specialty-card {
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: #fff;
  border: 1px solid #e8e0ee;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 14px;
  width: 100%;
  box-sizing: border-box;
}

.lea-specialty-fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.lea-field-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.lea-sublabel {
  font-size: 12px;
  font-weight: 600;
  color: #8c7d96;
}

.lea-input-full {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e2d5ef;
  border-radius: 8px;
  padding: 9px 14px;
  font-size: 13px;
  color: #3d2a4a;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.lea-input-full:focus {
  border-color: #6a1f4c;
  box-shadow: 0 0 0 3px rgba(106, 31, 76, 0.1);
}

.lea-specialty-desc-wrapper {
  position: relative;
  width: 100%;
}

.lea-specialty-desc-wrapper .lea-char-count {
  position: absolute;
  right: 12px;
  bottom: 10px;
  font-size: 11px;
  color: #9e8dae;
}

.lea-btn-remove-specialty-text {
  display: inline-flex !important;
  justify-content: flex-end !important;
  align-items: center !important;
  background: #fff !important;
  color: #6a1f4c !important;
  border: none !important;
  border-radius: 6px !important;
  padding: 6px 5px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  cursor: pointer !important;
}

.lea-btn-inner {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;

  .material-icons {
    font-size: 15px !important;
    margin-right: -115px !important;
  }
}

.lea-btn-inner span {
  font-size: 12px !important;
  font-weight: 600 !important;
  color: inherit !important;
  text-transform: none !important;
  margin: 0 !important;
  padding: 0 !important;
}
</style>
